import { useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'bw-ambient-muted'
const FADE_SECONDS = 2.5

const AmbientAudio = () => {
    const audioRef = useRef(null)
    const [playing, setPlaying] = useState(false)
    const interactionListenerRef = useRef(null)
    // Web Audio graph (created lazily on first play) + fade helper shared
    // between the initial-play effect and the toggle handler.
    const audioContextRef = useRef(null)
    const gainNodeRef = useRef(null)
    const sourceNodeRef = useRef(null)
    const rafRef = useRef(null)
    const fadeInRef = useRef(null)

    useEffect(() => {
        const userMuted = localStorage.getItem(STORAGE_KEY) === 'true'
        const audio = new Audio('/audio/buddhist-drone.wav')
        audio.loop = true
        // Keep the element at browser-max; the GainNode does the fade so this
        // also works on iOS Safari, where audio.volume is ignored.
        audio.volume = 1.0
        audioRef.current = audio

        // Build the Web Audio graph once: element -> gain -> destination.
        // createMediaElementSource may only be called a single time per element.
        const ensureGraph = () => {
            if (audioContextRef.current) return true
            const Ctx = window.AudioContext || window.webkitAudioContext
            if (!Ctx) return false
            try {
                const ctx = new Ctx()
                const source = ctx.createMediaElementSource(audio)
                const gain = ctx.createGain()
                gain.gain.value = 1.0
                source.connect(gain).connect(ctx.destination)
                audioContextRef.current = ctx
                sourceNodeRef.current = source
                gainNodeRef.current = gain
                return true
            } catch (err) {
                return false
            }
        }

        // Ramp 0 -> full over FADE_SECONDS. Runs only on an explicit start
        // (initial autoplay, delayed first-interaction start, or unmute) —
        // never on loop wraps, since loop=true never re-fires this.
        const fadeIn = () => {
            if (ensureGraph()) {
                const ctx = audioContextRef.current
                const gain = gainNodeRef.current
                if (ctx.state === 'suspended') ctx.resume()
                const now = ctx.currentTime
                gain.gain.cancelScheduledValues(now)
                gain.gain.setValueAtTime(0, now)
                gain.gain.linearRampToValueAtTime(1.0, now + FADE_SECONDS)
                return
            }
            // Fallback (no Web Audio): ramp the element volume via rAF.
            // iOS ignores audio.volume, so there it just plays at full instantly.
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
            const start = performance.now()
            audio.volume = 0
            const step = (t) => {
                const p = Math.min((t - start) / (FADE_SECONDS * 1000), 1)
                audio.volume = p
                if (p < 1) {
                    rafRef.current = requestAnimationFrame(step)
                } else {
                    rafRef.current = null
                    audio.volume = 1.0
                }
            }
            rafRef.current = requestAnimationFrame(step)
        }
        fadeInRef.current = fadeIn

        if (!userMuted) {
            audio.play().then(() => {
                fadeIn()
                setPlaying(true)
            }).catch(() => {
                // Autoplay blocked — start on first user interaction
                const onInteraction = () => {
                    audio.play().then(() => {
                        fadeIn()
                        setPlaying(true)
                    }).catch(() => {})
                    document.removeEventListener('click', onInteraction)
                    document.removeEventListener('keydown', onInteraction)
                    interactionListenerRef.current = null
                }
                interactionListenerRef.current = onInteraction
                document.addEventListener('click', onInteraction)
                document.addEventListener('keydown', onInteraction)
            })
        }

        return () => {
            audio.pause()
            audio.src = ''
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
            if (audioContextRef.current) {
                audioContextRef.current.close().catch(() => {})
                audioContextRef.current = null
            }
            if (interactionListenerRef.current) {
                document.removeEventListener('click', interactionListenerRef.current)
                document.removeEventListener('keydown', interactionListenerRef.current)
            }
        }
    }, [])

    const toggle = (e) => {
        e.stopPropagation()
        const audio = audioRef.current
        if (!audio) return

        if (playing) {
            audio.pause()
            setPlaying(false)
            localStorage.setItem(STORAGE_KEY, 'true')
        } else {
            // Clear any pending first-interaction listener
            if (interactionListenerRef.current) {
                document.removeEventListener('click', interactionListenerRef.current)
                document.removeEventListener('keydown', interactionListenerRef.current)
                interactionListenerRef.current = null
            }
            audio.play().then(() => {
                // Fade in on unmute too, so it isn't a jump-scare.
                if (fadeInRef.current) fadeInRef.current()
                setPlaying(true)
                localStorage.removeItem(STORAGE_KEY)
            }).catch(() => {})
        }
    }

    return (
        <button
            onClick={toggle}
            className="fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full bg-slate-800/80 backdrop-blur-sm border border-amber-500/30 text-white flex items-center justify-center hover:bg-slate-700/90 hover:border-amber-400/60 transition-all shadow-lg"
            title={playing ? 'Mute ambient sound' : 'Enable ambient sound'}
            aria-label={playing ? 'Mute ambient sound' : 'Enable ambient sound'}
        >
            {playing ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
            )}
        </button>
    )
}

export default AmbientAudio
